import { useState } from "react";
import { useForm } from "react-hook-form";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { AppointmentType, AppointmentTypeFormData } from "@/types/appointment";

export const AppointmentTypeConfig = () => {
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingType, setEditingType] = useState<AppointmentType | null>(null);
  const { toast } = useToast();

  const form = useForm<AppointmentTypeFormData>({
    defaultValues: {
      name: "",
      duration: 30,
      color: "#4F46E5",
      description: "",
    },
  });

  const loadAppointmentTypes = async () => {
    try {
      const { data, error } = await supabase
        .from("appointment_types")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      setAppointmentTypes(data || []);
    } catch (error) {
      console.error("Error loading appointment types:", error);
      toast({
        title: "Error",
        description: "Failed to load appointment types",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: AppointmentTypeFormData) => {
    try {
      if (editingType) {
        const { error } = await supabase
          .from("appointment_types")
          .update(data)
          .eq("id", editingType.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Appointment type updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("appointment_types")
          .insert([data]);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Appointment type created successfully",
        });
      }

      setIsOpen(false);
      form.reset();
      setEditingType(null);
      loadAppointmentTypes();
    } catch (error) {
      console.error("Error saving appointment type:", error);
      toast({
        title: "Error",
        description: "Failed to save appointment type",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("appointment_types")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast({
        title: "Success",
        description: "Appointment type deleted successfully",
      });
      loadAppointmentTypes();
    } catch (error) {
      console.error("Error deleting appointment type:", error);
      toast({
        title: "Error",
        description: "Failed to delete appointment type",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Appointment Types</CardTitle>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Type
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingType ? "Edit" : "Add"} Appointment Type
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Initial Consultation" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (minutes)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min={5}
                          step={5}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value, 10))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Color</FormLabel>
                      <FormControl>
                        <Input {...field} type="color" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Brief description of the appointment type"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full">
                  {editingType ? "Update" : "Create"} Appointment Type
                </Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {appointmentTypes.map((type) => (
            <div
              key={type.id}
              className="flex items-center justify-between p-4 border rounded-lg"
              style={{ borderLeftColor: type.color, borderLeftWidth: "4px" }}
            >
              <div>
                <h4 className="font-medium">{type.name}</h4>
                <p className="text-sm text-muted-foreground">
                  {type.duration} minutes
                </p>
                {type.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {type.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditingType(type);
                    form.reset({
                      name: type.name,
                      duration: type.duration,
                      color: type.color,
                      description: type.description,
                    });
                    setIsOpen(true);
                  }}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(type.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};